import { Button } from '@emailforge/ui';
import { useRef, useState } from 'react';

import { Icon } from '@/components/ui/icons';
import { useUploadAssetMutation } from '@/features/uploads/uploadsApi';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Reusable upload + URL field for image-y block props (hero.imageUrl,
 * image.src, product.imageUrl). Two ways to set a value:
 *   1. Drop / click to upload — pushes the file to the API, then writes
 *      the returned CDN URL into `value`.
 *   2. Paste a URL into the text input — bypasses the upload flow.
 *
 * Keeps the existing "paste URL" UX so users with their own CDNs aren't
 * forced through Spaces.
 */
export function ImageUploadField({
  value,
  onChange,
  onOpenLibrary,
}: {
  value: string;
  onChange: (url: string) => void;
  /** Optional callback to open an asset library drawer to pick a previously-uploaded asset. */
  onOpenLibrary?: () => void;
}) {
  const [upload, { isLoading }] = useUploadAssetMutation();
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setError(null);
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`${file.type || 'unknown type'} not allowed. Use JPEG, PNG, WebP, GIF, or AVIF.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB; max is 5 MB.`);
      return;
    }
    try {
      const asset = await upload(file).unwrap();
      onChange(asset.url);
    } catch (err) {
      const msg =
        (err as { data?: { message?: string } })?.data?.message ?? 'Upload failed. Try again.';
      setError(msg);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const f = e.dataTransfer.files[0];
          if (f) void handleFile(f);
        }}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `1px dashed ${isDragging ? 'var(--flame)' : 'var(--line)'}`,
          background: isDragging ? 'var(--flame-soft)' : 'var(--paper-2)',
          borderRadius: 'var(--r-2)',
          padding: 14,
          textAlign: 'center',
          cursor: isLoading ? 'wait' : 'pointer',
          fontSize: 12,
          color: 'var(--ink-3)',
          transition: 'border-color 100ms, background 100ms',
        }}
      >
        {value ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src={value}
              alt=""
              style={{
                width: 44,
                height: 44,
                objectFit: 'cover',
                borderRadius: 4,
                border: '1px solid var(--line)',
                background: 'white',
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  color: 'var(--ink-2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {value.split('/').pop()}
              </div>
              <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2 }}>
                Click to replace · drop a new file
              </div>
            </div>
          </div>
        ) : (
          <>
            <Icon.upload size={16} />
            <div style={{ marginTop: 4 }}>
              {isLoading ? 'Uploading…' : isDragging ? 'Drop here' : 'Drag image here or click to upload'}
            </div>
            <div className="mono" style={{ fontSize: 10, marginTop: 4 }}>
              JPEG · PNG · WebP · GIF · AVIF · max 5 MB
            </div>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = '';
          }}
        />
      </div>

      {error && (
        <div style={{ fontSize: 11, color: 'var(--err)', lineHeight: 1.4 }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          className="mf-input"
          type="url"
          value={value}
          placeholder="…or paste a URL"
          onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, fontSize: 12 }}
        />
        {onOpenLibrary && (
          <Button size="sm" variant="ghost" onClick={onOpenLibrary} title="Pick from your asset library">
            <Icon.folder size={12} />
          </Button>
        )}
      </div>
    </div>
  );
}
